import React, { useMemo, useState } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Input } from '@/components/ui/input';
import { formatApiError } from '@/utils/apiError';

/** Searchable stock-product picker with an inline "create new stock product" option. */
export function StockProductPicker({ products, suggestName, suggestCategory, onPick, onCreated, testId }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return products.filter((p) => p.name.toLowerCase().includes(t) || (p.category || '').toLowerCase().includes(t)).slice(0, 8);
  }, [q, products]);

  const create = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/products`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: suggestName, category: suggestCategory }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Could not create product');
      const created = d.product || d;
      onCreated?.(created);
      onPick(created.id);
      toast.success(`Created "${suggestName}" under ${suggestCategory}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); setOpen(false); }
  };

  return (
    <div className="relative w-64" data-testid={testId}>
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search stock products…" className="h-8 pl-7 text-sm bg-white" data-testid={`${testId}-input`} />
      {open && (matches.length > 0 || q.trim()) && (
        <ul className="absolute z-30 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-results`}>
          {matches.map((p) => (
            <li key={p.id}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(p.id); setQ(''); setOpen(false); }} className="w-full text-left px-3 py-1.5 hover:bg-purple-50" data-testid={`${testId}-result-${p.id}`}>
                <span className="text-sm font-semibold block truncate">{p.name}</span>
                <span className="text-[10px] text-gray-500">{p.category}{p.xero_code ? ` · Xero ${p.xero_code}` : ''}</span>
              </button>
            </li>
          ))}
          {suggestName && (
            <li className="border-t">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={create} disabled={busy} className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm font-semibold text-emerald-800 flex items-center gap-1.5" data-testid={`${testId}-create`}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create "{suggestName}" as a new stock product
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
